/**
 * Company: SPHMedia
 * Description: Garage Latest Layout
 */

import { ReactElement, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArticleDataObject } from "@app/types/Cue";
import Basic from "@components/ArticleCard/Basic";
import Header from "@components/Header/Header";
import LoadMore from "@components/LoadMore/LoadMore";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterSignUpPanel from "@components/Newsletter/Verticals/NewsletterSignUp";
import VerticalBlockTitle from "@components/VerticalBlockTitle/VerticalBlockTitle";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { fetchMoreStoriesData } from "@pages/Section/Section.server";
import type { SectionDefaultProps } from "@pages/Section/types";
import { compact } from "lodash-es";

import GarageBanner from "./components/GarageBanner";
import GarageNewsletterDescription from "./components/GarageNewsletterDescription";

export default function GarageVerticalLatest({
  context: {
    data: { title, overview },
  },
  parentCategory,
  childCategory,
}: SectionDefaultProps): ReactElement {
  useGlobalAdSetting({ targeting: { key: "page", value: "listing" } });
  const categoryName = childCategory
    ? `${parentCategory}/${childCategory}`
    : parentCategory;

  const [latestStories, setLatestStories] =
    useState<ArticleDataObject[]>(overview);
  const [hasMoreStories, setHasMoreStories] = useState(true);
  const uniqueCategoryQuery = compact([
    parentCategory !== "keywords" && parentCategory,
    childCategory,
  ]).join("_");

  const handleLoadMore = async () => {
    const moreStories = await fetchMoreStoriesData(
      uniqueCategoryQuery,
      parentCategory || "",
      latestStories.at(-1) as ArticleDataObject
    );
    if (moreStories.length < 10) setHasMoreStories(false);
    setLatestStories([...latestStories, ...moreStories]);
  };

  return (
    <div className="w-full">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <Header mastheadTitle={categoryName} />

      <GarageBanner />

      {/* Top Stories */}
      <div className="container px-[var(--gutter-x-sm)] py-[var(--gutter-y)]">
        <VerticalBlockTitle rootClassName="mb-4" text="Latest" />
        <ul
          data-testid="garage-vertical-latest-list"
          className={"flex flex-col divide-y"}
        >
          {latestStories.map((article) => {
            return (
              <li className="py-4" key={article.id}>
                <Basic
                  articleData={article}
                  showSection={false}
                  imagePosition="right"
                />
              </li>
            );
          })}
        </ul>
        <LoadMore
          rootClassName="my-4"
          onLoadMore={handleLoadMore}
          hasMore={hasMoreStories}
        />
      </div>

      <NewsletterSignUpPanel
        rootClassName="mb-3"
        descriptionComponent={<GarageNewsletterDescription />}
        newsletter={BT_NEWSLETTERS.filter(
          (newsletter) => newsletter.type === NEWSLETTER_TYPE.GARAGE
        )}
        btnColors="bg-verticals-garage text-white hover:bg-white hover:text-verticals-garage"
        linkColors="text-verticals-garage"
      />

      {/* Placeholder for Garage footer banner component, will be replaced once done */}
    </div>
  );
}
